import klass from './SectionTabs.module.scss';
import { CustomEventWithDetail, type Html, html, View } from 'rune-ts';
import { font } from '../../../typography';
import { PostMessageNavigate } from '../../../app/navigate';
import { dataStr } from '../../../util/dataStr';

export type SectionTabType = {
  id: string;
  href: string;
  label: string;
  count?: string | number;
  klass?: string;
  preventDefault?: boolean;
};

export interface SectionTabsProps {
  current_tab_id: string;
  tabs: SectionTabType[];
  size?: 'xLarge' | 'large' | 'medium' | 'small' | 'xSmall';
  custom_klass?: string;
  navigate?: PostMessageNavigate;

  /**
   * @default 'underline'
   */
  variants?: 'underline' | 'bubble';
}

export interface SectionTabProps {
  tab: SectionTabType;
  size?: SectionTabsProps['size'];
  navigate?: SectionTabsProps['navigate'];
  active: boolean;
}

class SectionTab extends View<SectionTabProps> {
  static readonly ClickTabEvent = CustomEventWithDetail<{ tabId: string; view: SectionTab }>;

  protected override template(data: SectionTabProps): Html {
    const { tab, active, navigate } = data;

    return html`
      <a
        href="${tab.href}"
        data-id="${tab.id}"
        data-active="${active}"
        class="${klass.tab}"
        ${navigate ? html`data-post-message="${dataStr(navigate)}"` : ''}
        ${tab.preventDefault ? html`onclick="event.preventDefault()"` : ''}
      >
        <!-- label -->
        <span>${tab.label}</span>

        <!-- count -->
        ${tab.count ? html`<span class="${klass.count}">${tab.count || ''}</spanc>` : ''}
      </a>
    `;
  }

  public setActive(active: boolean) {
    this.data.active = active;
    return this;
  }

  public getId() {
    return this.data.tab.id;
  }

  public setLabel(label: string) {
    this.data.tab.label = label;
    return this;
  }

  protected override onRender() {
    return this.addEventListener('click', (e) => {
      if (!this.data.active) {
        this.dispatchEvent(SectionTab.ClickTabEvent, {
          bubbles: true,
          detail: {
            view: this,
            tabId: this.data.tab.id,
          },
        });
      } else {
        e.preventDefault();
      }
    });
  }
}

export class SectionTabs extends View<SectionTabsProps> {
  static readonly ChangeTabEvent = CustomEventWithDetail<{ tabId: string }>;
  static readonly sizeOptions = {
    xLarge: {
      tab_font: font('28_bold'),
      size_class: klass.xLarge,
    },
    large: {
      tab_font: font('24_bold'),
      size_class: klass.large,
    },
    medium: {
      tab_font: font('20_bold'),
      size_class: klass.medium,
    },
    small: {
      tab_font: font('16_medium'),
      size_class: klass.small,
    },
    xSmall: {
      tab_font: font('14_medium'),
      size_class: klass.xSmall,
    },
  } as const;

  public tabViews = this.makeSectionTabViews();

  protected override template(data: SectionTabsProps) {
    const { size = this.size, custom_klass = '' } = data;
    const { tab_font, size_class } = SectionTabs.sizeOptions[size];

    const variants = this.data.variants === 'bubble' ? klass.bubble : klass.underline;

    return html`<div class="${klass.tabs} ${variants} ${tab_font} ${size_class} ${custom_klass}">
      ${this.tabViews}
    </div>`;
  }

  private makeSectionTabViews() {
    return this.data.tabs.map(
      (tab) =>
        new SectionTab({
          tab,
          size: this.data.size,
          active: tab.id === this.data.current_tab_id,
          navigate: this.data.navigate,
        }),
    );
  }

  get size() {
    return this.data.size || 'small';
  }

  protected override onRender() {
    return this.delegate(SectionTab.ClickTabEvent, SectionTab, (e) => {
      this.tabViews.forEach((view) => {
        view.setActive(e.detail.tabId === view.data.tab.id);
      });
      this.redraw();
      this.dispatchEvent(SectionTabs.ChangeTabEvent, {
        bubbles: true,
        detail: {
          tabId: e.detail.tabId,
        },
      });
    });
  }
}
